import {
  GamePackDeleteMutation,
  GamePackGetQuery,
  GamePackIcon,
  GamePackListQuery,
  GamePackPalette
} from '@/API';
import { gamePackDelete } from '@/graphql/mutations';
import { gamePackGet, gamePackList } from '@/graphql/queries';
import { generateClient, GraphQLQuery } from 'aws-amplify/api';

const client = generateClient();

// Game pack icons (big)
import iceBigIconUrl from '@/assets/images/gamepacks/big/ice.png';
import lightBulbBigIconUrl from '@/assets/images/gamepacks/big/lightBulb.png';
import pumpkinBigIconUrl from '@/assets/images/gamepacks/big/pumpkin.png';
import sunBigIconUrl from '@/assets/images/gamepacks/big/sun.png';
import orangeStarBigIconUrl from '@/assets/images/gamepacks/big/orangeStar.png';
import rocketBigIconUrl from '@/assets/images/gamepacks/big/rocket.png';
import heartBigIconUrl from '@/assets/images/gamepacks/big/heart.png';
import coffeeBigIconUrl from '@/assets/images/gamepacks/big/coffee.png';
import pencilBigIconUrl from '@/assets/images/gamepacks/big/pencil.png';
import greenLeafBigIconUrl from '@/assets/images/gamepacks/big/greenLeaf.png';
import greenStarBigIconUrl from '@/assets/images/gamepacks/big/greenStar.png';
import blocksBigIconUrl from '@/assets/images/gamepacks/big/blocks.png';
import earBigIconUrl from '@/assets/images/gamepacks/big/ear.png';
import leavesBigIconUrl from '@/assets/images/gamepacks/big/leaves.png';
import shootingStarBigIconUrl from '@/assets/images/gamepacks/big/shootingStar.png';

// Game pack icons (small)
import iceSmallIconUrl from '@/assets/images/gamepacks/small/ice.png';
import lightBulbSmallIconUrl from '@/assets/images/gamepacks/small/lightBulb.png';
import pumpkinSmallIconUrl from '@/assets/images/gamepacks/small/pumpkin.png';
import sunSmallIconUrl from '@/assets/images/gamepacks/small/sun.png';
import orangeStarSmallIconUrl from '@/assets/images/gamepacks/small/orangeStar.png';
import rocketSmallIconUrl from '@/assets/images/gamepacks/small/rocket.png';
import heartSmallIconUrl from '@/assets/images/gamepacks/small/heart.png';
import coffeeSmallIconUrl from '@/assets/images/gamepacks/small/coffee.png';
import pencilSmallIconUrl from '@/assets/images/gamepacks/small/pencil.png';
import greenLeafSmallIconUrl from '@/assets/images/gamepacks/small/greenLeaf.png';
import greenStarSmallIconUrl from '@/assets/images/gamepacks/small/greenStar.png';
import blocksSmallIconUrl from '@/assets/images/gamepacks/small/blocks.png';
import earSmallIconUrl from '@/assets/images/gamepacks/small/ear.png';
import leavesSmallIconUrl from '@/assets/images/gamepacks/small/leaves.png';
import shootingStarSmallIconUrl from '@/assets/images/gamepacks/small/shootingStar.png';

// Game pack icons (white)
import iceWhiteIconUrl from '@/assets/images/gamepacks/white/ice.png';
import lightBulbWhiteIconUrl from '@/assets/images/gamepacks/white/lightBulb.png';
import pumpkinWhiteIconUrl from '@/assets/images/gamepacks/white/pumpkin.png';
import sunWhiteIconUrl from '@/assets/images/gamepacks/white/sun.png';
import orangeStarWhiteIconUrl from '@/assets/images/gamepacks/white/orangeStar.png';
import rocketWhiteIconUrl from '@/assets/images/gamepacks/white/rocket.png';
import heartWhiteIconUrl from '@/assets/images/gamepacks/white/heart.png';
import coffeeWhiteIconUrl from '@/assets/images/gamepacks/white/coffee.png';
import pencilWhiteIconUrl from '@/assets/images/gamepacks/white/pencil.png';
import greenLeafWhiteIconUrl from '@/assets/images/gamepacks/white/greenLeaf.png';
import greenStarWhiteIconUrl from '@/assets/images/gamepacks/white/greenStar.png';
import blocksWhiteIconUrl from '@/assets/images/gamepacks/white/blocks.png';
import earWhiteIconUrl from '@/assets/images/gamepacks/white/ear.png';
import leavesWhiteIconUrl from '@/assets/images/gamepacks/white/leaves.png';
import shootingStarWhiteIconUrl from '@/assets/images/gamepacks/white/shootingStar.png';

export const defaultGamePack = 'Icebreaker';

export type GamePackPaletteOptions = {
  label: string;
  value: GamePackPalette;
  colors: {
    bg: string;
    selection: string;
    shapes: string;
    inverse: string;
    hover: string;
  };
};

export const loadingPaletteColors = {
  bg: 'bg-neutral-100',
  shapes: 'fill-neutral-200',
  inverse: 'fill-neutral-300',
  fill: 'fill-[#EBEBED]'
};

export const gamePackPaletteOptions: GamePackPaletteOptions[] = [
  {
    label: 'Blue',
    value: GamePackPalette.BLUE,
    colors: {
      bg: 'bg-secondary-sky-blue-100',
      selection: 'bg-secondary-sky-blue-600',
      shapes: 'fill-secondary-sky-blue-500',
      inverse: 'fill-secondary-clementine-500',
      hover: 'bg-secondary-sky-blue-500/20'
    }
  },
  {
    label: 'Orange',
    value: GamePackPalette.ORANGE,
    colors: {
      bg: 'bg-secondary-clementine-100',
      selection: 'bg-secondary-clementine-600',
      shapes: 'fill-secondary-clementine-500',
      inverse: 'fill-secondary-sky-blue-500',
      hover: 'bg-secondary-clementine-500/20'
    }
  },
  {
    label: 'Rose Red',
    value: GamePackPalette.ROSE_RED,
    colors: {
      bg: 'bg-primary-100',
      selection: 'bg-primary-600',
      shapes: 'fill-primary-500',
      inverse: 'fill-secondary-sky-blue-600',
      hover: 'bg-primary-500/20'
    }
  },
  {
    label: 'Red',
    value: GamePackPalette.RED,
    colors: {
      bg: 'bg-secondary-red-100',
      selection: 'bg-secondary-red-600',
      shapes: 'fill-secondary-red-500',
      inverse: 'fill-secondary-sky-blue-500',
      hover: 'bg-secondary-red-500/20'
    }
  },
  {
    label: 'Light Green',
    value: GamePackPalette.LIGHT_GREEN,
    colors: {
      bg: 'bg-accent-1-100',
      selection: 'bg-accent-1-600',
      shapes: 'fill-accent-1-500',
      inverse: 'fill-secondary-clementine-500',
      hover: 'bg-accent-1-500/20'
    }
  },
  {
    label: 'Green',
    value: GamePackPalette.GREEN,
    colors: {
      bg: 'bg-secondary-hunter-green-100',
      selection: 'bg-secondary-hunter-green-600',
      shapes: 'fill-secondary-hunter-green-500',
      inverse: 'fill-accent-1-500',
      hover: 'bg-secondary-hunter-green-500/20'
    }
  },
  {
    label: 'Dark Green',
    value: GamePackPalette.DARK_GREEN,
    colors: {
      bg: 'bg-secondary-seafoam-100',
      selection: 'bg-secondary-seafoam-600',
      shapes: 'fill-secondary-seafoam-500',
      inverse: 'fill-secondary-clementine-500',
      hover: 'bg-secondary-seafoam-500/20'
    }
  },
  {
    label: 'Navy Blue',
    value: GamePackPalette.NAVY_BLUE,
    colors: {
      bg: 'bg-secondary-navy-100',
      selection: 'bg-secondary-navy-600',
      shapes: 'fill-secondary-navy-500',
      inverse: 'fill-secondary-sky-blue-500',
      hover: 'bg-secondary-navy-500/20'
    }
  }
];

type GamePackIconOptions = {
  label: string;
  value: GamePackIcon;
  icons: {
    big: string;
    small: string;
    white: string;
  };
};

export const gamePackIconOptions: GamePackIconOptions[] = [
  {
    label: 'Ice',
    value: GamePackIcon.ICE,
    icons: {
      big: iceBigIconUrl,
      small: iceSmallIconUrl,
      white: iceWhiteIconUrl
    }
  },
  {
    label: 'Light Bulb',
    value: GamePackIcon.LIGHT_BULB,
    icons: {
      big: lightBulbBigIconUrl,
      small: lightBulbSmallIconUrl,
      white: lightBulbWhiteIconUrl
    }
  },
  {
    label: 'Pumpkin',
    value: GamePackIcon.PUMPKIN,
    icons: {
      big: pumpkinBigIconUrl,
      small: pumpkinSmallIconUrl,
      white: pumpkinWhiteIconUrl
    }
  },
  {
    label: 'Sun',
    value: GamePackIcon.SUN,
    icons: {
      big: sunBigIconUrl,
      small: sunSmallIconUrl,
      white: sunWhiteIconUrl
    }
  },
  {
    label: 'Orange Star',
    value: GamePackIcon.ORANGE_STAR,
    icons: {
      big: orangeStarBigIconUrl,
      small: orangeStarSmallIconUrl,
      white: orangeStarWhiteIconUrl
    }
  },
  {
    label: 'Rocket',
    value: GamePackIcon.ROCKET,
    icons: {
      big: rocketBigIconUrl,
      small: rocketSmallIconUrl,
      white: rocketWhiteIconUrl
    }
  },
  {
    label: 'Heart',
    value: GamePackIcon.HEART,
    icons: {
      big: heartBigIconUrl,
      small: heartSmallIconUrl,
      white: heartWhiteIconUrl
    }
  },
  {
    label: 'Coffee',
    value: GamePackIcon.COFFEE,
    icons: {
      big: coffeeBigIconUrl,
      small: coffeeSmallIconUrl,
      white: coffeeWhiteIconUrl
    }
  },
  {
    label: 'Pencil',
    value: GamePackIcon.PENCIL,
    icons: {
      big: pencilBigIconUrl,
      small: pencilSmallIconUrl,
      white: pencilWhiteIconUrl
    }
  },
  {
    label: 'Green Leaf',
    value: GamePackIcon.GREEN_LEAF,
    icons: {
      big: greenLeafBigIconUrl,
      small: greenLeafSmallIconUrl,
      white: greenLeafWhiteIconUrl
    }
  },
  {
    label: 'Green Star',
    value: GamePackIcon.GREEN_STAR,
    icons: {
      big: greenStarBigIconUrl,
      small: greenStarSmallIconUrl,
      white: greenStarWhiteIconUrl
    }
  },
  {
    label: 'Blocks',
    value: GamePackIcon.BLOCKS,
    icons: {
      big: blocksBigIconUrl,
      small: blocksSmallIconUrl,
      white: blocksWhiteIconUrl
    }
  },
  {
    label: 'Ear',
    value: GamePackIcon.EAR,
    icons: {
      big: earBigIconUrl,
      small: earSmallIconUrl,
      white: earWhiteIconUrl
    }
  },
  {
    label: 'Leaves',
    value: GamePackIcon.LEAVES,
    icons: {
      big: leavesBigIconUrl,
      small: leavesSmallIconUrl,
      white: leavesWhiteIconUrl
    }
  },
  {
    label: 'Shooting Star',
    value: GamePackIcon.SHOOTING_STAR,
    icons: {
      big: shootingStarBigIconUrl,
      small: shootingStarSmallIconUrl,
      white: shootingStarWhiteIconUrl
    }
  }
];

export const guideGamePacks = ['Icebreaker', 'Team Building', 'Innovation', 'Workplace Check-in'];

// Keeping the title field - it's the same for now, but may change in the future
export const guideGamePacksNames: { [key: string]: { title: string; name: string } } = {
  Icebreaker: {
    title: 'Great! Let’s dive in',
    name: 'Break the ice'
  },
  'Team Building': {
    title: 'Great! Let’s dive in',
    name: 'Team Building'
  },
  Innovation: {
    title: 'Great! Let’s dive in',
    name: 'Spark Innovation'
  },
  'Workplace Check-in': {
    title: 'Great! Let’s dive in',
    name: 'Wellness Check-In'
  }
};

export const getSelectedGamePackPalette = (key: GamePackPalette) => {
  return gamePackPaletteOptions.find((palette) => palette.value === key);
};

export const getSelectedGamePackIcon = (key: GamePackIcon) => {
  return gamePackIconOptions.find((icon) => icon.value === key);
};

export const elementsTransition = { type: 'spring', stiffness: 300, damping: 20, mass: 1 };

// Get All Game Packs
export const listAllGamePacks = async () => {
  return client.graphql<GraphQLQuery<GamePackListQuery>>({
    query: gamePackList,
    variables: {
      filter: { deletedAt: { attributeExists: false } }
    }
  });
};

// Get Game Packs
export const getGamePack = async (id: string) => {
  return client.graphql<GraphQLQuery<GamePackGetQuery>>({
    query: gamePackGet,
    variables: {
      id
    }
  });
};

export const listGamePackHost = async () => {
  return client.graphql<GraphQLQuery<GamePackListQuery>>({
    query: gamePackList,
    variables: {
      filter: { deletedAt: { attributeExists: false } }
    }
  });
};

export const removeGamePack = async (id: string) => {
  return client.graphql<GraphQLQuery<GamePackDeleteMutation>>({
    query: gamePackDelete,
    variables: { id }
  });
};

// export const createNewGamePackOrganizationAssociation = (
//   gamePackId: string,
//   organizationId: string
// ) => {
//   return client.graphql<GraphQLQuery<CreateGamePackOrganizationsMutation>>({
//     query: createGamePackOrganizations,
//     variables: {
//       input: {
//         gamePackId,
//         organizationId
//       }
//     }
//   });
// };

// export const deleteGamePackOrganizationAssociation = (gamePackOrganizationId: string) => {
//   return client.graphql<GraphQLQuery<DeleteGamePackOrganizationsMutation>>({
//     query: deleteGamePackOrganizations,
//     variables: {
//       input: {
//         id: gamePackOrganizationId
//       }
//     }
//   });
// };

// export const createNewGamePack = (
//   gamePack: Partial<GamePack>,
//   groups: string[],
//   orgs: Partial<Organization>[]
// ) => {
//   return client
//     .graphql<GraphQLQuery<CreateGamePackMutation>>({
//       query: createGamePack,
//       variables: {
//         input: {
//           name: gamePack.name,
//           description: gamePack.description,
//           image: gamePack.image,
//           status: GamePackStatus.active,
//           groups: groups,
//           isFeatured: false // TODO: not yet implemented in UI
//         }
//       }
//     })
//     .then(async (result) => {
//       const gamePackId = result.data.createGamePack?.id;
//       if (gamePackId && orgs && orgs.length > 0) {
//         const associationPromises = orgs.map(
//           (org) => org.id && createNewGamePackOrganizationAssociation(gamePackId, org.id)
//         );
//         await Promise.all(associationPromises);
//       }
//       return result;
//     });
// };

// export const updateExistingGamePack = (
//   gamePack: Partial<GamePack>,
//   groups: string[],
//   /** Associations with organizations to create */
//   orgsToCreate?: Partial<Organization>[],
//   /** Existing associations with organizations to remove, requires only the association ID */
//   gamePackOrganizationAssociationsToRemove?: (string | undefined)[]
// ) => {
//   if (!gamePack.id) {
//     throw new Error('GamePack id not provide');
//   }

// const updatePromise = client.graphql<GraphQLQuery<UpdateGamePackMutation>>({
//   query: updateGamePack,
//   variables: {
//     input: {
//       id: gamePack.id,
//       name: gamePack.name,
//       description: gamePack.description,
//       image: gamePack.image,
//       status: gamePack.status,
//       groups: groups,
//       isFeatured: false // TODO: not yet implemented in UI
//     }
//   }
// });

//   const createGamePackOrgPromises =
//     orgsToCreate?.map((org) => createNewGamePackOrganizationAssociation(gamePack.id!, org.id!)) ??
//     [];

//   const removeGamePackOrgPromises =
//     gamePackOrganizationAssociationsToRemove?.map((id) =>
//       id ? deleteGamePackOrganizationAssociation(id) : new Promise<void>((resolve) => resolve())
//     ) ?? [];

//   return Promise.all([updatePromise, ...createGamePackOrgPromises, ...removeGamePackOrgPromises]);
// };
